import {isEmpty} from "lodash"
import {SearchApiResponse} from "../../models/searchApi"
import {ProductItem} from "../../models/Product"
import createProductSummaryEsiTag, {
    createProductSummaryJsonEsiTag,
    productSummaryEsiBaseUrl,
    productSummaryJsonEsiBaseUrl,
} from "../createProductSummaryEsiTag"
import {SearchState} from "../../models/SearchState"
import {LAZYLOADING_TYPE} from "../../models/Lazyload"
import {
    ESISeparator,
    FRAGMENT_ITEMS_QUERY_STRING,
    ITEMS_STRING_QUERY_STRING,
    PRODUCT_TYPE,
    QUERY_PARAMETER_IMAGE_RATIO,
    QUERY_PARAMETER_TEMPLATE_OVERRIDE,
} from "../../config/constants"
import {SummaryData} from "../../models/ProductSummary"
import {FeatureSwitch} from "../../models/FeatureSwitch"
import {isJson} from "../isJson"

const productSummaryEsiUrl = (siteUrl: string, useDevEsi: boolean) =>
    `${productSummaryEsiBaseUrl(siteUrl, useDevEsi)}/product-summary/$(type)/$(number)?show-new-in=$(newin)`

const productSummaryJsonEsiUrl = (siteUrl: string, useDevEsi: boolean, apiProxyVersion: string) =>
    `${productSummaryJsonEsiBaseUrl(
        siteUrl,
        useDevEsi,
    )}/plp-ui/api/${apiProxyVersion}/product-summary/$(type)/$(number)`

/**
 * Creates a products fragment string that can be later converted into an
 * array of product items using the `mapProductsFragmentToItems()` function
 * @param items
 */
export const createProductsFragment = (
    items: ProductItem[],
    siteUrl: string,
    useDevEsi: boolean,
    imQueryParam: string,
    featureSwitch: FeatureSwitch,
    apiProxyVersion: string,
    saleOrClearance: "sale" | "clearance" | "",
) => {
    if (featureSwitch?.enableNewProductSummary) {
        const productSummaryJson = items.reduce((acc: string, item) => {
            const {itemNumber, newIn, type = PRODUCT_TYPE} = item
            const esiTag = createProductSummaryJsonEsiTag(siteUrl, useDevEsi, apiProxyVersion, itemNumber, type)
            return `${acc}<==>${itemNumber}<=>${newIn}<=>${type}<=>${esiTag}`
        }, "")
        return productSummaryJson
    }

    return items.reduce((acc: string, item) => {
        const {itemNumber, newIn, type, productSummaryTemplateName, title, imageRatio} = item
        const esiTag = createProductSummaryEsiTag(
            itemNumber,
            newIn,
            siteUrl,
            useDevEsi,
            type,
            saleOrClearance,
            title,
            productSummaryTemplateName,
            imageRatio,
            imQueryParam,
        )
        return `${acc}<==>${itemNumber}<=>${newIn}<=>${type}<=>${esiTag}`
    }, "")
}

export const appendFilterData = (fragments: string, filters: SearchApiResponse) => {
    return `${JSON.stringify(filters)}${ESISeparator.ProductFragment}${fragments}`
}

export interface MapProductsFragmentToItemsResponse {
    items: MapProductsFragmentToItemsResponseItems[]
    searchStateData: SearchState | null
    searchBannerHtml: string | null
    searchBannerJson: string | null
}
interface MapProductsFragmentToItemsResponseItems {
    itemNumber: string
    newIn: boolean
    html: string
    type: string
}

export type MapProductsFragmentJsonToItemsResponseItems = Pick<
    SummaryData,
    | "brand"
    | "category"
    | "colourways"
    | "currencyCode"
    | "department"
    | "fit"
    | "id"
    | "itemNumber"
    | "newIn"
    | "title"
    | "type"
>

export interface MapProductsFragmentJsonToItemsResponse {
    items: MapProductsFragmentJsonToItemsResponseItems[]
    searchStateData: SearchState | null
    searchBannerHtml: string | null
    searchBannerJson: string | null
}

/**
 * Converts a products fragment string to an array of product items
 * @param fragment
 */
export const mapProductsFragmentToItems = (
    apiResponse: string,
    lazyloadItemIndexFrom: number,
    lazyloadType: LAZYLOADING_TYPE = "no-lazyload",
): MapProductsFragmentToItemsResponse => {
    let filters: string | null = null
    let fragments: string | null = null
    let searchBanner: string | null = null
    let other: string
        // eslint-disable-next-line prefer-const
    ;[other, searchBanner] = apiResponse.split(ESISeparator.SearchBanner)
    if (!searchBanner) {
        searchBanner = null
    }
    if (apiResponse.includes(ESISeparator.ProductFragment)) {
        ;[filters, fragments] = other.split(ESISeparator.ProductFragment)
    } else {
        fragments = apiResponse
    }

    const searchStateData = filters ? (JSON.parse(filters) as SearchState) : null

    return {
        items: fragments
            .split(ESISeparator.ProductArray)[0]
            .split("<==>")
            .reduce((acc, item, key) => {
                const trimmedItem = item.trim()

                if (!trimmedItem) return acc

                const parts = trimmedItem.split("<=>")

                const html = parts[3]

                acc.push({
                    itemNumber: parts[0],
                    newIn: parts[1] === "true",
                    html,
                    type: parts[2] || PRODUCT_TYPE,
                })

                return acc
            }, [] as MapProductsFragmentToItemsResponseItems[]),
        searchStateData,
        searchBannerHtml: searchBanner,
        searchBannerJson: searchStateData?.searchBannerJson ?? null,
    }
}

export const mapProductsFragmentJsonToItems = (apiResponse: string): MapProductsFragmentJsonToItemsResponse => {
    let filters: string | null = null
    let fragments: string | null = null
    let searchBanner: string | null = null
    let other: string
        // eslint-disable-next-line prefer-const
    ;[other, searchBanner] = apiResponse.split(ESISeparator.SearchBanner)
    if (!searchBanner) {
        searchBanner = null
    }
    if (apiResponse.includes(ESISeparator.ProductFragment)) {
        ;[filters, fragments] = other.split(ESISeparator.ProductFragment)
    } else {
        fragments = apiResponse
    }

    const searchStateData = filters ? (JSON.parse(filters) as SearchState) : null

    return {
        items: fragments
            .split(ESISeparator.ProductArray)[0]
            .split("<==>")
            .reduce((acc, item) => {
                const trimmedItem = item.trim()
                if (!trimmedItem) return acc

                const parts = trimmedItem.split("<=>")
                const stringedJson = parts[3]

                const productSummaryData: MapProductsFragmentJsonToItemsResponseItems =
                    // eslint-disable-next-line no-nested-ternary
                    stringedJson ? (isJson(stringedJson) ? JSON.parse(stringedJson) : {}) : {}

                const summaryData = (
                    !isEmpty(productSummaryData)
                        ? {
                              ...productSummaryData,
                              itemNumber: parts[0],
                              newIn: parts[1] === "true",
                              type: (parts[2] || PRODUCT_TYPE) as "product" | "suit" | "sofa",
                          }
                        : {}
                ) as MapProductsFragmentJsonToItemsResponseItems

                acc.push(summaryData)

                return acc
            }, [] as MapProductsFragmentJsonToItemsResponseItems[]),
        searchStateData,
        searchBannerHtml: searchBanner,
        searchBannerJson: searchStateData?.searchBannerJson ?? null,
    }
}

export const replaceHTMLProductImagesLazyloadState = (productSummaryHTML: string) => {
    const regex = /"productImages":false/g
    return productSummaryHTML.replace(regex, `"productImages":true`)
}

export const createStaticEsiHtml = (siteUrl: string, useDevEsi: boolean) => `
    <esi:foreach collection="$string_split($(QUERY_STRING{'${FRAGMENT_ITEMS_QUERY_STRING}'}),',')">
        <esi:assign name="product" value="$string_split($(item),'|')" />
        <esi:assign name="type" value="$(product{0})" />
        <esi:assign name="number" value="$(product{1})" />
        <esi:assign name="newin" value="$(product{2})" />
        <esi:assign name="productSummaryTemplateName" value="$(product{3})" />
        <esi:assign name="imageRatio" value="$(product{4})" />
        <==>$(number)<=>$(newin)<=>$(type)<=>
        <esi:choose>
            <esi:when test="$(productSummaryTemplateName) && $(imageRatio)">
                <esi:include src="${productSummaryEsiUrl(
                    siteUrl,
                    useDevEsi,
                )}&${QUERY_PARAMETER_TEMPLATE_OVERRIDE}=$(productSummaryTemplateName)&${QUERY_PARAMETER_IMAGE_RATIO}=$(imageRatio)" onerror="continue" dca="none" />
            </esi:when>
            <esi:when test="$(productSummaryTemplateName)">
                <esi:include src="${productSummaryEsiUrl(
                    siteUrl,
                    useDevEsi,
                )}&${QUERY_PARAMETER_TEMPLATE_OVERRIDE}=$(productSummaryTemplateName)" onerror="continue" dca="none" />
            </esi:when>
            <esi:when test="$(imageRatio)">
                <esi:include src="${productSummaryEsiUrl(
                    siteUrl,
                    useDevEsi,
                )}&${QUERY_PARAMETER_IMAGE_RATIO}=$(imageRatio)" onerror="continue" dca="none" />
            </esi:when>
            <esi:otherwise>
                <esi:include src="${productSummaryEsiUrl(siteUrl, useDevEsi)}" onerror="continue" dca="none" />
            </esi:otherwise>
        </esi:choose>
    </esi:foreach>
`

export const createStaticEsiJson = (siteUrl: string, useDevEsi: boolean, apiProxyVersion: string) => `
        <esi:foreach collection="$string_split($(QUERY_STRING{'${ITEMS_STRING_QUERY_STRING}'}),',')">
            <esi:assign name="product" value="$string_split($(item),'|')" />
            <esi:assign name="type" value="$(product{0})" />
            <esi:assign name="number" value="$(product{1})" />
            <esi:assign name="newin" value="$(product{2})" />
            <==>$(number)<=>$(newin)<=>$(type)<=>
            <esi:include src="${productSummaryJsonEsiUrl(
                siteUrl,
                useDevEsi,
                apiProxyVersion,
            )}" onerror="continue" dca="none" />
        </esi:foreach>
`
